'use client';

import { Suspense, useRef, type ComponentProps, type ReactNode } from 'react';
import { Await } from 'react-router';
import { Region } from '@/components/region';
import type { ContentSlotPageWithComponentData } from '@/extensions/snk/lib/content-slot-page';
import { FOOTER_PAYMENT_METHOD_REGION_ID } from '@/features/global-footer/constants';
import { PAYMENT_METHOD_IDS } from '@/extensions/snk/lib/payment-method-platforms';
import { cn } from '@/lib/utils';
import { LogoStack } from '@/shared-ui';
import { PaymentMethodIcon } from '../payment-method-icon';

export interface PaymentMethodIconListProps extends Omit<ComponentProps<'div'>, 'children'> {
    heading: string;
    icons?: ReactNode;
}

export const PaymentMethodIconList = ({ heading, icons, className, ...props }: PaymentMethodIconListProps) => {
    const trimmedHeading = heading.trim();
    if (!trimmedHeading) {
        return null;
    }

    return (
        <LogoStack.Root className={cn(className)} data-slot="payment-method-icon-list" {...props}>
            <LogoStack.Title className="text-primary-foreground">{trimmedHeading}</LogoStack.Title>
            <LogoStack.Wrapper>
                <div data-slot="payment-method-icon-row" className="flex flex-row flex-wrap gap-2">
                    {icons}
                </div>
            </LogoStack.Wrapper>
        </LogoStack.Root>
    );
};

function PaymentMethodsStatic({ className }: { className?: string }) {
    return (
        <PaymentMethodIconList
            className={className}
            heading="We accept"
            icons={PAYMENT_METHOD_IDS.map((method) => (
                <PaymentMethodIcon key={method} method={method} />
            ))}
        />
    );
}

type PaymentMethodsProps = {
    globalContentSlots?: Promise<ContentSlotPageWithComponentData>;
    className?: string;
};

export const PaymentMethods = ({ globalContentSlots, className }: PaymentMethodsProps) => {
    const pageRef = useRef<Promise<ContentSlotPageWithComponentData> | undefined>(undefined);

    if (!globalContentSlots) {
        return <PaymentMethodsStatic className={className} />;
    }

    if (!pageRef.current) {
        pageRef.current = globalContentSlots;
    }

    const page = pageRef.current;

    return (
        <div className={className}>
            <Suspense fallback={null}>
                <Await resolve={page} errorElement={null}>
                    {(resolved) => (
                        <Region
                            page={resolved}
                            regionId={FOOTER_PAYMENT_METHOD_REGION_ID}
                            fallbackElement={null}
                            errorElement={null}
                        />
                    )}
                </Await>
            </Suspense>
        </div>
    );
};
